import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Chip,
  Alert,
  LinearProgress,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Link
} from '@mui/material';
import { 
  Add as AddIcon, 
  Upload as UploadIcon, 
  Download as DownloadIcon,
  DirectionsCar as CarIcon,
  Info as InfoIcon,
  ArrowUpward,
  ArrowDownward,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Phone as PhoneIcon,
  Groups as GroupsIcon,
  List as ListIcon
} from '@mui/icons-material';
import DataTable from '../components/common/DataTable';
import { NumberTextField } from '../components/common';
import { insuranceClientService } from '../services/insuranceClientService';
import { insuranceCompanyService } from '../services/insuranceCompanyService';
import { servicePackageService } from '../services/servicePackageService';
import { useTranslation } from 'react-i18next';
import { usePagination } from '../hooks/usePagination';
import UploadProgressDialog, { UploadProgressEvent } from '../components/UploadProgressDialog';
import { handleSSEUpload } from '../utils/sseHelper';

const InsuranceClients: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pagination, setPagination, handlePageChange, handlePageSizeChange } = usePagination(10);
  const [clients, setClients] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [servicePackages, setServicePackages] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [currentClient, setCurrentClient] = useState<any>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [uploadProgress, setUploadProgress] = useState<any>(null);
  const [sortField, setSortField] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const progressDialogRef = useRef<any>(null);
  const [overviewDialogOpen, setOverviewDialogOpen] = useState(false);
  const [selectedClientForOverview, setSelectedClientForOverview] = useState<any>(null);
  const [viewMode, setViewMode] = useState<'list' | 'fleet'>('list');
  const [fleetGroups, setFleetGroups] = useState<{ policyNumber: string; clients: any[]; companyName?: string }[]>([]);
  const [fleetLoading, setFleetLoading] = useState(false);
  const [expandedFleetPolicy, setExpandedFleetPolicy] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    customerName: '',
    vinNumber: '',
    plateNumber: '',
    insuranceCompany: '',
    servicePackage: '',
    isActive: '',
    carMake: '',
    carModel: '',
    policyNumber: ''
  });
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const [formData, setFormData] = useState({
    vinNumber: '',
    customerName: '',
    phoneNumber: '',
    email: '',
    plateNumber: '',
    carMake: '',
    carModel: '',
    carYear: '',
    policyNumber: '',
    policyStartDate: '',
    policyEndDate: '',
    servicePackage: '',
    insuranceCompany: '',
    notes: '',
    isActive: true
  });

  // Create stable filter key to prevent infinite loops
  const filterKey = useMemo(() => JSON.stringify(filters), [
    filters.customerName,
    filters.vinNumber,
    filters.plateNumber,
    filters.insuranceCompany,
    filters.servicePackage,
    filters.isActive,
    filters.carMake,
    filters.carModel,
    filters.policyNumber
  ]);

  useEffect(() => {
    fetchCompanies();
    fetchServicePackages();
  }, []);

  useEffect(() => {
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.pageSize, sortField, sortOrder, filterKey]);

  // When switching to fleet view, fetch fleets (only policy numbers with 2+ clients)
  useEffect(() => {
    if (viewMode !== 'fleet') return;
    let cancelled = false;
    const run = async () => {
      setFleetLoading(true);
      try {
        const response = await insuranceClientService.getFleets();
        const data = response?.data || [];
        const groups = Array.isArray(data) ? data : [];
        if (cancelled) return;
        // API returns only fleets with >1 client; ensure we never show single-record groups
        const multiOnly = groups.filter((g: any) => (g.clients?.length || 0) > 1);
        setFleetGroups(multiOnly);
      } catch (e) {
        if (!cancelled) {
          console.error('Error fetching fleet data:', e);
          setFleetGroups([]);
        }
      } finally {
        if (!cancelled) setFleetLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [viewMode]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      
      // Build query parameters
      const params: any = {
        'pagination[page]': pagination.page + 1, // Strapi uses 1-based indexing
        'pagination[pageSize]': pagination.pageSize,
      };

      // Add sorting
      const sortParam = sortOrder === 'asc' ? `${sortField}:asc` : `${sortField}:desc`;
      params.sort = sortParam;

      // Add filters
      if (filters.customerName) {
        params['filters[customerName][$containsi]'] = filters.customerName;
      }
      if (filters.vinNumber) {
        params['filters[vinNumber][$containsi]'] = filters.vinNumber;
      }
      if (filters.plateNumber) {
        params['filters[plateNumber][$containsi]'] = filters.plateNumber;
      }
      if (filters.insuranceCompany) {
        params['filters[insuranceCompany][id][$eq]'] = filters.insuranceCompany;
      }
      if (filters.servicePackage) {
        params['filters[servicePackage][id][$eq]'] = filters.servicePackage;
      }
      if (filters.isActive !== '') {
        params['filters[isActive][$eq]'] = filters.isActive === 'true';
      }
      if (filters.carMake) {
        params['filters[carMake][$containsi]'] = filters.carMake;
      }
      if (filters.carModel) {
        params['filters[carModel][$containsi]'] = filters.carModel;
      }
      if (filters.policyNumber) {
        params['filters[policyNumber][$containsi]'] = filters.policyNumber;
      }

      const response = await insuranceClientService.getAll(params);
      
      // Handle Strapi v4 response format: { data: [...], meta: { pagination: {...} } }
      const clientsData = response?.data || [];
      const paginationMeta = response?.meta?.pagination || {};
      
      setClients(Array.isArray(clientsData) ? clientsData : []);
      setPagination({
        total: paginationMeta.total || 0,
        pageCount: paginationMeta.pageCount || 0,
      });
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]);
      setPagination({ total: 0, pageCount: 0 });
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await insuranceCompanyService.getAll();
      // Handle Strapi v4 response format: { data: [...], meta: {...} }
      const companiesData = response?.data || response || [];
      setCompanies(Array.isArray(companiesData) ? companiesData : []);
    } catch (error) {
      console.error('Error fetching companies:', error);
      setCompanies([]);
    }
  };

  const fetchServicePackages = async () => {
    try {
      const response = await servicePackageService.getAll();
      // Handle Strapi v4 response format: { data: [...], meta: {...} }
      const packagesData = response?.data || response || [];
      setServicePackages(Array.isArray(packagesData) ? packagesData : []);
    } catch (error) {
      console.error('Error fetching service packages:', error);
      setServicePackages([]);
    }
  };

  const handleOpenDialog = (client: any = null) => {
    if (client) {
      // Handle Strapi v4 format (with attributes) or plain format
      const clientData = client.attributes || client;
      let insuranceCompany = clientData.insuranceCompany || client.insuranceCompany;
      
      // Handle REST API format where relations are nested in data
      if (insuranceCompany?.data) {
        insuranceCompany = insuranceCompany.data;
      }
      
      // Extract servicePackage ID from relation
      let servicePackageId = '';
      const servicePackage = clientData.servicePackage || client.servicePackage;
      if (servicePackage) {
        if (servicePackage.data) {
          servicePackageId = servicePackage.data.id || servicePackage.data.attributes?.id || '';
        } else if (servicePackage.id) {
          servicePackageId = servicePackage.id;
        } else if (servicePackage.attributes?.id) {
          servicePackageId = servicePackage.attributes.id;
        } else if (typeof servicePackage === 'number') {
          servicePackageId = servicePackage.toString();
        }
      }
      
      setCurrentClient(client);
      setFormData({
        vinNumber: clientData.vinNumber || client.vinNumber || '',
        customerName: clientData.customerName || client.customerName || '',
        phoneNumber: clientData.phoneNumber || client.phoneNumber || '',
        email: clientData.email || client.email || '',
        plateNumber: clientData.plateNumber || client.plateNumber || '',
        carMake: clientData.carMake || client.carMake || '',
        carModel: clientData.carModel || client.carModel || '',
        carYear: clientData.carYear || client.carYear || '',
        policyNumber: clientData.policyNumber || client.policyNumber || '',
        policyStartDate: clientData.policyStartDate || client.policyStartDate || '',
        policyEndDate: clientData.policyEndDate || client.policyEndDate || '',
        servicePackage: servicePackageId,
        insuranceCompany: insuranceCompany?.id || insuranceCompany?.attributes?.id || '',
        notes: clientData.notes || client.notes || '',
        isActive: clientData.isActive !== undefined ? clientData.isActive : (client.isActive !== undefined ? client.isActive : true)
      });
    } else {
      setCurrentClient(null);
      setFormData({
        vinNumber: '',
        customerName: '',
        phoneNumber: '',
        email: '',
        plateNumber: '',
        carMake: '',
        carModel: '',
        carYear: '',
        policyNumber: '',
        policyStartDate: '',
        policyEndDate: '',
        servicePackage: '',
        insuranceCompany: '',
        notes: '',
        isActive: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentClient(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Clean up form data - convert empty strings to null and handle types
      const cleanedData: any = {
        vinNumber: formData.vinNumber?.trim() || null,
        customerName: formData.customerName?.trim() || null,
        phoneNumber: formData.phoneNumber?.trim() || null,
        email: formData.email?.trim() || null,
        plateNumber: formData.plateNumber?.trim() || null,
        carMake: formData.carMake?.trim() || null,
        carModel: formData.carModel?.trim() || null,
        carYear: formData.carYear ? (parseInt(formData.carYear.toString()) || null) : null,
        policyNumber: formData.policyNumber?.trim() || null,
        policyStartDate: formData.policyStartDate || null,
        policyEndDate: formData.policyEndDate || null,
        servicePackage: formData.servicePackage ? (parseInt(formData.servicePackage.toString()) || null) : null,
        insuranceCompany: formData.insuranceCompany ? (parseInt(formData.insuranceCompany.toString()) || null) : null,
        notes: formData.notes?.trim() || null,
        isActive: formData.isActive
      };

      // Remove empty strings and undefined values
      Object.keys(cleanedData).forEach(key => {
        if (cleanedData[key] === '' || cleanedData[key] === undefined) {
          cleanedData[key] = null;
        }
      });

      // Ensure vinNumber is not null (required field)
      if (!cleanedData.vinNumber) {
        alert('VIN Number is required');
        return;
      }

      if (currentClient) {
        // Handle Strapi v4 format - get ID from either format
        const clientId = currentClient.id || currentClient.attributes?.id;
        await insuranceClientService.update(clientId, cleanedData);
      } else {
        await insuranceClientService.create(cleanedData);
      }
      handleCloseDialog();
      fetchClients();
    } catch (error: any) {
      console.error('Error saving client:', error);
      const errorMessage = error.response?.data?.error?.message || error.message || 'Unknown error occurred';
      alert(`Error saving client: ${errorMessage}`);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm(t('confirmDelete'))) {
      try {
        await insuranceClientService.delete(id);
        fetchClients();
      } catch (error) {
        console.error('Error deleting client:', error);
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setUploadFile(e.target.files[0]);
    }
  };

  const handleDownloadTemplate = async () => {
    if (!selectedCompanyId) {
      alert('Please select a company first');
      return;
    }

    try {
      await insuranceClientService.downloadTemplate(selectedCompanyId);
    } catch (error) {
      console.error('Error downloading template:', error);
      alert('Error downloading template: ' + (error as any).message);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !selectedCompanyId) {
      alert('Please select a file and company');
      return;
    }

    try {
      // Close upload dialog and open progress dialog
      setOpenUploadDialog(false);
      setProgressDialogOpen(true);

      const { url, formData } = insuranceClientService.getUploadStreamUrl(uploadFile, selectedCompanyId);

      // Use SSE helper to handle streaming upload
      await handleSSEUpload(url, formData, {
        onEvent: (event) => {
          // Pass event to progress dialog
          if (progressDialogRef.current && progressDialogRef.current.handleEvent) {
            progressDialogRef.current.handleEvent(event);
          }

          // Refresh client list when complete
          if (event.type === 'complete') {
            fetchClients();
          }
        },
        onError: (error) => {
          console.error('Upload error:', error);
          alert(`Error uploading file: ${error.message}`);
          setProgressDialogOpen(false);
        },
        onComplete: () => {
          console.log('Upload stream completed');
          // Reset upload state
          setUploadFile(null);
          setSelectedCompanyId('');
        }
      });
      
    } catch (error: any) {
      console.error('Error uploading file:', error);
      const errorMessage = error.message || 'Unknown error occurred';
      alert(`Error uploading file: ${errorMessage}`);
      setProgressDialogOpen(false);
    }
  };


  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    // Reset to first page when sorting changes
    handlePageChange(0);
  };

  const handleFilterChange = (field: string, value: any) => {
    setFilters({ ...filters, [field]: value });
    // Reset to first page when filters change
    handlePageChange(0);
  };

  const clearFilters = () => {
    setFilters({
      customerName: '',
      vinNumber: '',
      plateNumber: '',
      insuranceCompany: '',
      servicePackage: '',
      isActive: '',
      carMake: '',
      carModel: '',
      policyNumber: ''
    });
    // Reset to first page when filters are cleared
    handlePageChange(0);
  };

  // No need for client-side filtering/sorting anymore as it's done server-side

  const columns = [
    { 
      field: 'vinNumber', 
      headerName: 'VIN Number',
      flex: 1
    },
    { 
      field: 'customerName', 
      headerName: t('customerName'),
      flex: 1
    },
    { 
      field: 'phoneNumber', 
      headerName: 'Phone Number',
      width: 150,
      renderCell: (params: any) => {
        if (!params.value) return '-';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PhoneIcon fontSize="small" color="action" />
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {params.value}
            </Typography>
          </Box>
        );
      }
    },
    { 
      field: 'plateNumber', 
      headerName: 'Plate Number',
      width: 120
    },
    { 
      field: 'carMake', 
      headerName: 'Car Make',
      width: 120
    },
    { 
      field: 'carModel', 
      headerName: 'Car Model',
      width: 120
    },
    { 
      field: 'insuranceCompany', 
      headerName: 'Insurance Company',
      flex: 1,
      renderCell: (params: any) => {
        let company = params.value;
        if (!company) return '-';
        
        // Handle different Strapi v4 formats
        // If it's already been processed by DataTable and is a string, return it
        if (typeof company === 'string') {
          return company;
        }
        
        // Handle REST API format: { data: { id, attributes: { name } } }
        if (company.data) {
          company = company.data;
        }
        
        // Handle attributes wrapper
        if (company.attributes) {
          return company.attributes.name || company.attributes.id || '-';
        }
        
        // Handle direct format: { id, name }
        return company.name || company.id || '-';
      }
    },
    { 
      field: 'servicePackage', 
      headerName: 'Service Package',
      width: 150,
      renderCell: (params: any) => {
        let packageData = params.value;
        if (!packageData) return '-';
        
        // Handle different Strapi v4 formats
        // If it's already been processed by DataTable and is a string, return it
        if (typeof packageData === 'string') {
          return packageData;
        }
        
        // Handle REST API format: { data: { id, attributes: { name, code } } }
        if (packageData.data) {
          packageData = packageData.data;
        }
        
        // Handle attributes wrapper
        if (packageData.attributes) {
          return packageData.attributes.name || packageData.attributes.code || '-';
        }
        
        // Handle direct format: { id, name, code }
        return packageData.name || packageData.code || packageData.id || '-';
      }
    },
    { 
      field: 'policyNumber', 
      headerName: 'Policy Number',
      width: 150,
      renderCell: (params: any) => {
        if (!params.value) return '-';
        return (
          <Box 
            sx={{ 
              fontSize: '0.75rem',
              maxWidth: '150px',
              overflow: 'auto',
              whiteSpace: 'nowrap',
              '&::-webkit-scrollbar': {
                height: '4px'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.2)',
                borderRadius: '4px'
              }
            }}
          >
            {params.value}
          </Box>
        );
      }
    },
    { 
      field: 'policyEndDate', 
      headerName: 'Policy Expiry',
      width: 120,
      renderCell: (params: any) => {
        if (!params.value) return '-';
        const date = new Date(params.value);
        const isExpired = date < new Date();
        return (
          <Chip 
            label={date.toLocaleDateString()} 
            color={isExpired ? 'error' : 'success'}
            size="small"
          />
        );
      }
    },
    { 
      field: 'isActive', 
      headerName: t('status'),
      width: 100,
      renderCell: (params: any) => (
        <Chip 
          label={params.value ? t('active') : t('inactive')} 
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CarIcon color="primary" />
          <Typography variant="h4">
            Insurance Clients
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant={viewMode === 'fleet' ? 'contained' : 'outlined'}
            startIcon={<GroupsIcon />}
            onClick={() => setViewMode('fleet')}
          >
            Fleet
          </Button>
          <Button
            variant={viewMode === 'list' ? 'contained' : 'outlined'}
            startIcon={<ListIcon />}
            onClick={() => setViewMode('list')}
          >
            List
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => setOpenUploadDialog(true)}
          >
            Upload Excel
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            {t('addClient')}
          </Button>
        </Box>
      </Box>

      {/* Fleet view: groups of clients by policy number */}
      {viewMode === 'fleet' && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GroupsIcon color="primary" />
              Fleets (clients grouped by Policy Number)
            </Typography>
            {fleetLoading ? (
              <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                <LinearProgress sx={{ width: '100%', maxWidth: 400 }} />
              </Box>
            ) : fleetGroups.length === 0 ? (
              <Alert severity="info">No fleets found. A fleet is a policy number shared by more than one client. Only policy numbers with 2+ clients are listed here.</Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={48} />
                      <TableCell><strong>Policy Number</strong></TableCell>
                      <TableCell><strong>Insurance Company</strong></TableCell>
                      <TableCell align="right"><strong>Clients</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fleetGroups.map((group) => {
                      const isExpanded = expandedFleetPolicy === group.policyNumber;
                      return (
                        <React.Fragment key={group.policyNumber}>
                          <TableRow
                            hover
                            sx={{ '& > *': { borderBottom: 'unset' } }}
                            onClick={() => setExpandedFleetPolicy(isExpanded ? null : group.policyNumber)}
                          >
                            <TableCell>
                              <IconButton size="small">
                                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {group.policyNumber}
                              </Typography>
                            </TableCell>
                            <TableCell>{group.companyName || '-'}</TableCell>
                            <TableCell align="right">
                              <Chip label={group.clients.length} size="small" color="primary" />
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={4} sx={{ py: 0, borderBottom: isExpanded ? undefined : 0 }}>
                              <Collapse in={isExpanded} unmountOnExit>
                                <Table size="small" sx={{ mb: 2 }}>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>VIN</TableCell>
                                      <TableCell>Customer</TableCell>
                                      <TableCell>Plate</TableCell>
                                      <TableCell>Phone</TableCell>
                                      <TableCell>Car</TableCell>
                                      <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {group.clients.map((client: any) => {
                                      const attrs = client.attributes || client;
                                      const clientId = client.id || attrs?.id;
                                      return (
                                        <TableRow key={clientId} hover>
                                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                            {attrs.vinNumber || '-'}
                                          </TableCell>
                                          <TableCell>{attrs.customerName || '-'}</TableCell>
                                          <TableCell>{attrs.plateNumber || '-'}</TableCell>
                                          <TableCell>{attrs.phoneNumber || '-'}</TableCell>
                                          <TableCell>
                                            {[attrs.carMake, attrs.carModel, attrs.carYear].filter(Boolean).join(' ') || '-'}
                                          </TableCell>
                                          <TableCell align="right">
                                            <Button
                                              size="small"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (clientId) navigate(`/insurance-clients/${clientId}/overview`);
                                              }}
                                            >
                                              View
                                            </Button>
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filters and Sorting Section (List view only) */}
      {viewMode === 'list' && (<><Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterListIcon />
              <Typography variant="h6">Filters & Sorting</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                onClick={() => setFiltersExpanded(!filtersExpanded)}
                endIcon={filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              >
                {filtersExpanded ? 'Hide Filters' : 'Show Filters'}
              </Button>
              <Button
                size="small"
                onClick={clearFilters}
                startIcon={<ClearIcon />}
                disabled={Object.values(filters).every(v => v === '')}
              >
                Clear Filters
              </Button>
            </Box>
          </Box>

          <Collapse in={filtersExpanded}>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: 2, 
              mb: 2 
            }}>
              <TextField
                fullWidth
                size="small"
                label="Customer Name"
                value={filters.customerName}
                onChange={(e) => handleFilterChange('customerName', e.target.value)}
              />
              <TextField
                fullWidth
                size="small"
                label="VIN Number"
                value={filters.vinNumber}
                onChange={(e) => handleFilterChange('vinNumber', e.target.value)}
              />
              <TextField
                fullWidth
                size="small"
                label="Plate Number"
                value={filters.plateNumber}
                onChange={(e) => handleFilterChange('plateNumber', e.target.value)}
              />
              <TextField
                fullWidth
                size="small"
                label="Policy Number"
                value={filters.policyNumber}
                onChange={(e) => handleFilterChange('policyNumber', e.target.value)}
              />
              <FormControl fullWidth size="small">
                <InputLabel>Insurance Company</InputLabel>
                <Select
                  value={filters.insuranceCompany}
                  label="Insurance Company"
                  onChange={(e) => handleFilterChange('insuranceCompany', e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {companies.map((company: any) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name || company.attributes?.name || 'Unnamed Company'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Service Package</InputLabel>
                <Select
                  value={filters.servicePackage}
                  label="Service Package"
                  onChange={(e) => handleFilterChange('servicePackage', e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {servicePackages.map((pkg: any) => {
                    const pkgData = pkg.attributes || pkg;
                    return (
                      <MenuItem key={pkg.id} value={pkg.id}>
                        {pkgData.name || pkgData.code || `Package ${pkg.id}`}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                size="small"
                label="Car Make"
                value={filters.carMake}
                onChange={(e) => handleFilterChange('carMake', e.target.value)}
              />
              <TextField
                fullWidth
                size="small"
                label="Car Model"
                value={filters.carModel}
                onChange={(e) => handleFilterChange('carModel', e.target.value)}
              />
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.isActive}
                  label="Status"
                  onChange={(e) => handleFilterChange('isActive', e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Collapse>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 1 }}>Sort by:</Typography>
            <Button
              variant={sortField === 'createdAt' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => handleSort('createdAt')}
              startIcon={sortField === 'createdAt' && (sortOrder === 'asc' ? <ArrowUpward /> : <ArrowDownward />)}
            >
              Date
            </Button>
            <Button
              variant={sortField === 'customerName' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => handleSort('customerName')}
              startIcon={sortField === 'customerName' && (sortOrder === 'asc' ? <ArrowUpward /> : <ArrowDownward />)}
            >
              Name
            </Button>
            <Button
              variant={sortField === 'vinNumber' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => handleSort('vinNumber')}
              startIcon={sortField === 'vinNumber' && (sortOrder === 'asc' ? <ArrowUpward /> : <ArrowDownward />)}
            >
              VIN
            </Button>
            <Button
              variant={sortField === 'insuranceCompany' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => handleSort('insuranceCompany')}
              startIcon={sortField === 'insuranceCompany' && (sortOrder === 'asc' ? <ArrowUpward /> : <ArrowDownward />)}
            >
              Company
            </Button>
            <Button
              variant={sortField === 'policyEndDate' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => handleSort('policyEndDate')}
              startIcon={sortField === 'policyEndDate' && (sortOrder === 'asc' ? <ArrowUpward /> : <ArrowDownward />)}
            >
              Policy Expiry
            </Button>
            <Button
              variant={sortField === 'carMake' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => handleSort('carMake')}
              startIcon={sortField === 'carMake' && (sortOrder === 'asc' ? <ArrowUpward /> : <ArrowDownward />)}
            >
              Car Make
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Showing {clients.length} of {pagination.total} clients
            </Typography>
          </Box>
          <DataTable
            columns={columns}
            rows={clients}
            loading={loading}
            onEdit={handleOpenDialog}
            onDelete={handleDelete}
            onView={(client: any) => {
              // Navigate to customer overview page
              const clientId = client.id || client.attributes?.id;
              if (clientId) {
                navigate(`/insurance-clients/${clientId}/overview`);
              }
            }}
            page={pagination.page}
            rowsPerPage={pagination.pageSize}
            totalCount={pagination.total}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handlePageSizeChange}
          />
        </CardContent>
      </Card>

      </>)}

      {/* Overview Dialog */}
      <Dialog open={overviewDialogOpen} onClose={() => setOverviewDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5">Client Overview</Typography>
            {selectedClientForOverview && (
              <Chip 
                label={selectedClientForOverview.isActive !== false ? 'Active' : 'Inactive'} 
                color={selectedClientForOverview.isActive !== false ? 'success' : 'default'}
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedClientForOverview && (() => {
            const client = selectedClientForOverview.attributes || selectedClientForOverview;
            // Handle Strapi v4 format where relations are nested in data property
            const requestsData = client.requests?.data || client.requests || [];
            const requests = Array.isArray(requestsData) ? requestsData : [];
            const serviceUsagesData = client.serviceUsages?.data || client.serviceUsages || [];
            const serviceUsages = Array.isArray(serviceUsagesData) ? serviceUsagesData : [];
            const insuranceCompany = client.insuranceCompany?.data?.attributes || client.insuranceCompany?.attributes || client.insuranceCompany;
            const servicePackage = client.servicePackage?.data?.attributes || client.servicePackage?.attributes || client.servicePackage;

            return (
              <Box sx={{ mt: 2 }}>
                {/* Client Information Section */}
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>Client Information</Typography>
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                  gap: 2, 
                  mb: 3 
                }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">VIN Number</Typography>
                    <Typography variant="body1" fontWeight="medium">{client.vinNumber || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Customer Name</Typography>
                    <Typography variant="body1" fontWeight="medium">{client.customerName || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Phone Number</Typography>
                    <Typography variant="body1">{client.phoneNumber || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Email</Typography>
                    <Typography variant="body1">{client.email || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Plate Number</Typography>
                    <Typography variant="body1">{client.plateNumber || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Car Make</Typography>
                    <Typography variant="body1">{client.carMake || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Car Model</Typography>
                    <Typography variant="body1">{client.carModel || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Car Year</Typography>
                    <Typography variant="body1">{client.carYear || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Insurance Company</Typography>
                    <Typography variant="body1">{insuranceCompany?.name || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Service Package</Typography>
                    <Typography variant="body1">{servicePackage?.name || servicePackage?.code || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Policy Number</Typography>
                    <Typography variant="body1">{client.policyNumber || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Policy Start Date</Typography>
                    <Typography variant="body1">
                      {client.policyStartDate ? new Date(client.policyStartDate).toLocaleDateString() : '-'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Policy End Date</Typography>
                    <Typography variant="body1">
                      {client.policyEndDate ? (
                        <>
                          {new Date(client.policyEndDate).toLocaleDateString()}
                          {new Date(client.policyEndDate) < new Date() && (
                            <Chip label="Expired" color="error" size="small" sx={{ ml: 1 }} />
                          )}
                        </>
                      ) : '-'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Created At</Typography>
                    <Typography variant="body1">
                      {client.createdAt ? new Date(client.createdAt).toLocaleString() : '-'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Updated At</Typography>
                    <Typography variant="body1">
                      {client.updatedAt ? new Date(client.updatedAt).toLocaleString() : '-'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Data Source</Typography>
                    <Typography variant="body1">
                      {client.importedAt || client.dataSource ? (
                        <Chip 
                          label={client.importedAt ? 'Imported via Excel' : client.dataSource || 'Manual Entry'} 
                          size="small" 
                          color="info"
                        />
                      ) : 'Manual Entry'}
                    </Typography>
                  </Box>
                </Box>
                {client.notes && (
                  <Box sx={{ mt: 2, mb: 3 }}>
                    <Typography variant="caption" color="text.secondary">Notes</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                      {client.notes}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 3 }} />

                {/* Requests Section */}
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Service Requests ({requests.length})
                </Typography>
                {requests.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Request #</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Service Type</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell>Service Provider</TableCell>
                          <TableCell>Created Date</TableCell>
                          <TableCell>Priority</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {requests
                          .sort((a: any, b: any) => {
                            const aDate = new Date(a.createdAt || a.attributes?.createdAt || 0);
                            const bDate = new Date(b.createdAt || b.attributes?.createdAt || 0);
                            return bDate.getTime() - aDate.getTime();
                          })
                          .map((request: any, idx: number) => {
                            const req = request.attributes || request;
                            const provider = req.serviceProvider?.data?.attributes || req.serviceProvider?.attributes || req.serviceProvider;
                            const statusColors: any = {
                              pending: 'default',
                              assigned: 'info',
                              active: 'primary',
                              invoice_creation: 'warning',
                              invoice_sent: 'warning',
                              payment_received: 'success',
                              completed: 'success',
                              cancelled: 'error'
                            };
                            const priorityColors: any = {
                              low: 'default',
                              medium: 'info',
                              high: 'warning',
                              urgent: 'error'
                            };
                            const requestId = request.id || request.attributes?.id;
                            const displayRequestNumber = req.requestNumber || (requestId ? `#${requestId}` : '-');
                            return (
                              <TableRow key={idx} hover>
                                <TableCell>
                                  {requestId ? (
                                    <Link
                                      component="button"
                                      variant="body2"
                                      onClick={() => {
                                        navigate(`/requests`, { state: { requestId, openDetails: true } });
                                      }}
                                      sx={{ 
                                        cursor: 'pointer', 
                                        textDecoration: 'underline',
                                        color: 'primary.main',
                                        '&:hover': {
                                          textDecoration: 'underline',
                                          color: 'primary.dark'
                                        }
                                      }}
                                    >
                                      {displayRequestNumber}
                                    </Link>
                                  ) : (
                                    displayRequestNumber
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={req.status || 'pending'} 
                                    color={statusColors[req.status] || 'default'}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>{req.serviceType || req.serviceCode || '-'}</TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {req.description || '-'}
                                  </Typography>
                                </TableCell>
                                <TableCell>{provider?.name || provider?.businessName || '-'}</TableCell>
                                <TableCell>
                                  {req.createdAt ? new Date(req.createdAt).toLocaleDateString() : '-'}
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={req.priority || 'medium'} 
                                    color={priorityColors[req.priority] || 'default'}
                                    size="small"
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Alert severity="info" sx={{ mb: 3 }}>No service requests found for this client.</Alert>
                )}

                <Divider sx={{ my: 3 }} />

                {/* Service Usages Section */}
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Service Usage ({serviceUsages.length})
                </Typography>
                {serviceUsages.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, maxHeight: 300 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Service Name</TableCell>
                          <TableCell>Service Code</TableCell>
                          <TableCell>Used</TableCell>
                          <TableCell>Allowed</TableCell>
                          <TableCell>Remaining</TableCell>
                          <TableCell>Last Used</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {serviceUsages.map((usage: any, idx: number) => {
                          const usg = usage.attributes || usage;
                          const used = usg.usedCount || 0;
                          const allowed = usg.allowedCount || 0;
                          const remaining = allowed - used;
                          
                          // Try to get last used date from lastUsedDate or usageDates array
                          let lastUsedDate = null;
                          if (usg.lastUsedDate) {
                            lastUsedDate = new Date(usg.lastUsedDate);
                          } else if (usg.usageDates) {
                            // usageDates might be a JSON array of dates
                            try {
                              const dates = typeof usg.usageDates === 'string' ? JSON.parse(usg.usageDates) : usg.usageDates;
                              if (Array.isArray(dates) && dates.length > 0) {
                                // Get the most recent date
                                const sortedDates = dates
                                  .map((d: any) => new Date(d))
                                  .filter((d: Date) => !isNaN(d.getTime()))
                                  .sort((a: Date, b: Date) => b.getTime() - a.getTime());
                                if (sortedDates.length > 0) {
                                  lastUsedDate = sortedDates[0];
                                }
                              }
                            } catch (e) {
                              // If parsing fails, ignore
                            }
                          }
                          
                          return (
                            <TableRow key={idx} hover>
                              <TableCell>{usg.serviceName || '-'}</TableCell>
                              <TableCell>{usg.serviceCode || '-'}</TableCell>
                              <TableCell>
                                <Chip label={used} color={used > 0 ? 'primary' : 'default'} size="small" />
                              </TableCell>
                              <TableCell>{allowed}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={remaining} 
                                  color={remaining > 0 ? 'success' : 'error'} 
                                  size="small" 
                                />
                              </TableCell>
                              <TableCell>
                                {lastUsedDate ? lastUsedDate.toLocaleDateString() : 'Never'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Alert severity="info" sx={{ mb: 3 }}>No service usage records found for this client.</Alert>
                )}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {currentClient ? t('editClient') : t('addClient')}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
              <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                <TextField
                  fullWidth
                  label="VIN Number"
                  value={formData.vinNumber}
                  onChange={(e) => setFormData({ ...formData, vinNumber: e.target.value })}
                  required
                  disabled={!!currentClient}
                />
              </Box>
              <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                <TextField
                  fullWidth
                  label={t('customerName')}
                  value={formData.customerName}
                  onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                  required
                />
              </Box>
              <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                <TextField
                  fullWidth
                  label={t('phone')}
                  value={formData.phoneNumber}
                  onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                />
              </Box>
              <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                <TextField
                  fullWidth
                  label={t('email')}
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </Box>
              <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                <TextField
                  fullWidth
                  label="Plate Number"
                  value={formData.plateNumber}
                  onChange={(e) => setFormData({ ...formData, plateNumber: e.target.value })}
                />
              </Box>
              <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                <FormControl fullWidth required>
                  <InputLabel>Insurance Company</InputLabel>
                  <Select
                    value={formData.insuranceCompany}
                    label="Insurance Company"
                    onChange={(e) => setFormData({ ...formData, insuranceCompany: e.target.value })}
                  >
                    {companies.map((company: any) => (
                      <MenuItem key={company.id} value={company.id}>
                        {company.name || company.attributes?.name || 'Unnamed Company'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: '180px' }}>
                <TextField
                  fullWidth
                  label="Car Make"
                  value={formData.carMake}
                  onChange={(e) => setFormData({ ...formData, carMake: e.target.value })}
                />
              </Box>
              <Box sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: '180px' }}>
                <TextField
                  fullWidth
                  label="Car Model"
                  value={formData.carModel}
                  onChange={(e) => setFormData({ ...formData, carModel: e.target.value })}
                />
              </Box>
              <Box sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: '180px' }}>
                <NumberTextField
                  fullWidth
                  label="Car Year"
                  value={formData.carYear}
                  onChange={(e) => setFormData({ ...formData, carYear: e.target.value })}
                  allowDecimal={false}
                />
              </Box>
              <Box sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: '180px' }}>
                <TextField
                  fullWidth
                  label="Policy Number"
                  value={formData.policyNumber}
                  onChange={(e) => setFormData({ ...formData, policyNumber: e.target.value })}
                />
              </Box>
              <Box sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: '180px' }}>
                <TextField
                  fullWidth
                  label="Policy Start Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={formData.policyStartDate}
                  onChange={(e) => setFormData({ ...formData, policyStartDate: e.target.value })}
                />
              </Box>
              <Box sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: '180px' }}>
                <TextField
                  fullWidth
                  label="Policy End Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={formData.policyEndDate}
                  onChange={(e) => setFormData({ ...formData, policyEndDate: e.target.value })}
                />
              </Box>
              <Box sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: '180px' }}>
                <FormControl fullWidth>
                  <InputLabel>Service Package</InputLabel>
                  <Select
                    value={formData.servicePackage}
                    label="Service Package"
                    onChange={(e) => setFormData({ ...formData, servicePackage: e.target.value })}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {servicePackages.map((pkg: any) => {
                      const pkgData = pkg.attributes || pkg;
                      return (
                        <MenuItem key={pkg.id} value={pkg.id}>
                          {pkgData.name || pkgData.code || `Package ${pkg.id}`}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ width: '100%' }}>
                <TextField
                  fullWidth
                  label={t('notes')}
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>{t('cancel')}</Button>
            <Button type="submit" variant="contained">
              {t('save')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={openUploadDialog} onClose={() => setOpenUploadDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Import Insurance Clients</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
            <Box sx={{ width: '100%' }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Select an insurance company to import clients. The system will automatically detect the Excel structure based on the selected company.
                </Typography>
              </Alert>
            </Box>
            <Box sx={{ width: '100%' }}>
              <FormControl fullWidth required>
                <InputLabel>Insurance Company</InputLabel>
                <Select
                  value={selectedCompanyId}
                  label="Insurance Company"
                  onChange={(e) => setSelectedCompanyId(e.target.value)}
                >
                  {companies.map((company: any) => {
                    const companyName = company.name || company.attributes?.name || 'Unnamed Company';
                    const excelStructure = company.excelStructure || company.attributes?.excelStructure;
                    return (
                      <MenuItem key={company.id} value={company.id}>
                        {companyName} ({excelStructure?.toUpperCase() || 'N/A'})
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ width: '100%' }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadTemplate}
                disabled={!selectedCompanyId}
              >
                Download Template
              </Button>
            </Box>
            <Box sx={{ width: '100%' }}>
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileChange}
                style={{ display: 'none' }}
                id="upload-file"
              />
              <label htmlFor="upload-file">
                <Button
                  fullWidth
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                >
                  {uploadFile ? uploadFile.name : 'Select File'}
                </Button>
              </label>
            </Box>
            {uploadProgress && (
              <Box sx={{ width: '100%' }}>
                <Alert 
                  severity={uploadProgress.errors.length > 0 ? 'warning' : 'success'} 
                  sx={{ mb: 2 }}
                >
                  <Typography variant="body2" fontWeight="bold" gutterBottom>
                    Import Results:
                  </Typography>
                  <Typography variant="body2">
                    Total Rows: {uploadProgress.total} | Created: {uploadProgress.created} | Updated: {uploadProgress.updated}
                  </Typography>
                  {uploadProgress.errors.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="error" fontWeight="bold">
                        Errors: {uploadProgress.errors.length}
                      </Typography>
                      <Box component="ul" sx={{ mt: 1, pl: 2, maxHeight: 150, overflow: 'auto' }}>
                        {uploadProgress.errors.slice(0, 10).map((err: any, idx: number) => {
                          const errorMessage = typeof err.error === 'string' 
                            ? err.error 
                            : err.error?.message || JSON.stringify(err.error) || 'Unknown error';
                          return (
                            <li key={idx}>
                              <Typography variant="caption" color="error">
                                Row {err.row}: {errorMessage}
                              </Typography>
                            </li>
                          );
                        })}
                        {uploadProgress.errors.length > 10 && (
                          <Typography variant="caption" color="error">
                            ... and {uploadProgress.errors.length - 10} more errors
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                </Alert>
              </Box>
            )}
            {loading && (
              <Box sx={{ width: '100%', mt: 2 }}>
                <LinearProgress />
                <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                  Processing file...
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setOpenUploadDialog(false);
              setUploadFile(null);
              setSelectedCompanyId('');
              setUploadProgress(null);
            }}
          >
            {uploadProgress ? 'Close' : 'Cancel'}
          </Button>
          {!uploadProgress && (
            <Button 
              onClick={handleUpload} 
              variant="contained"
              disabled={!uploadFile || !selectedCompanyId || loading}
            >
              {loading ? 'Uploading...' : 'Upload'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Upload Progress Dialog */}
      <UploadProgressDialog
        open={progressDialogOpen}
        onClose={() => setProgressDialogOpen(false)}
        title="Insurance Clients Upload Progress"
        ref={progressDialogRef}
      />
    </Box>
  );
};

export default InsuranceClients;

